/**
 * Type definitions for Persona Studio
 * Bridges frontend WizardPersona types with backend API schemas
 */

// Re-export types from wizard for use in Persona Studio
export type {
  WizardPersona,
  PersonaDecisions,
  PersonaWorldview,
  PersonaVoice,
  DecisionDimension,
  DecisionLevel,
  ClaimOwnership,
  Confidence,
  CallToAction,
  Vulnerability,
  Energy,
  Formality,
  Specificity,
} from '../poc/wizards/types';

export {
  DECISION_DIMENSIONS,
  CHANNELS,
  TRAIT_SUGGESTIONS,
  SAMPLE_PROMPTS,
  ARCHETYPES,
  defaultPersona,
  defaultDecisions,
  defaultVoice,
  defaultWorldview,
  generateMockContent,
} from '../poc/wizards/types';

// =============================================================================
// API TYPES (matching backend schema)
// =============================================================================

export interface ApiPersonaWorldview {
  core_beliefs: string[];
  values: string[];
  audience: string;
  purpose: string;
}

export interface ApiPersonaVoice {
  traits: string[];
  description: string;
  example_phrases: string[];
  avoid_phrases: string[];
}

export interface ApiPersonaDimensions {
  claim_ownership: string;
  confidence: string;
  call_to_action: string;
  vulnerability: string;
  energy: string;
  formality: string;
  specificity: string;
}

export interface ApiPersonaCreate {
  name: string;
  worldview: ApiPersonaWorldview;
  voice: ApiPersonaVoice;
  dimensions: ApiPersonaDimensions;
}

export interface ApiPersonaUpdate {
  name?: string;
  worldview?: ApiPersonaWorldview;
  voice?: ApiPersonaVoice;
  dimensions?: ApiPersonaDimensions;
}

export interface ApiPersonaResponse {
  id: string;
  name: string;
  worldview: ApiPersonaWorldview;
  voice: ApiPersonaVoice;
  dimensions: ApiPersonaDimensions;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// FRONTEND PERSONA (with id for persistence)
// =============================================================================

export interface Persona {
  id: string;
  name: string;
  avatar?: string;
  tagline?: string;
  worldview: {
    coreBeliefs: string[];
    values: string[];
    audience: string;
    purpose: string;
  };
  voice: {
    traits: string[];
    description: string;
    examplePhrases: string[];
    avoidPhrases: string[];
  };
  decisions: {
    claimOwnership: string;
    confidence: string;
    callToAction: string;
    vulnerability: string;
    energy: string;
    formality: string;
    specificity: string;
  };
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// TRANSFORMERS
// =============================================================================

/**
 * Transform frontend persona to API create/update format
 */
export function toApiPersona(persona: Partial<Persona> & { name: string }): ApiPersonaCreate {
  return {
    name: persona.name,
    worldview: {
      core_beliefs: persona.worldview?.coreBeliefs ?? [],
      values: persona.worldview?.values ?? [],
      audience: persona.worldview?.audience ?? '',
      purpose: persona.worldview?.purpose ?? '',
    },
    voice: {
      traits: persona.voice?.traits ?? [],
      description: persona.voice?.description ?? '',
      example_phrases: persona.voice?.examplePhrases ?? [],
      avoid_phrases: persona.voice?.avoidPhrases ?? [],
    },
    dimensions: {
      claim_ownership: persona.decisions?.claimOwnership ?? 'personal',
      confidence: persona.decisions?.confidence ?? 'steady',
      call_to_action: persona.decisions?.callToAction ?? 'open',
      vulnerability: persona.decisions?.vulnerability ?? 'balanced',
      energy: persona.decisions?.energy ?? 'engaged',
      formality: persona.decisions?.formality ?? 'conversational',
      specificity: persona.decisions?.specificity ?? 'balanced',
    },
  };
}

/**
 * Transform API response to frontend persona format
 */
export function fromApiPersona(api: ApiPersonaResponse): Persona {
  return {
    id: api.id,
    name: api.name,
    worldview: {
      coreBeliefs: api.worldview.core_beliefs,
      values: api.worldview.values,
      audience: api.worldview.audience,
      purpose: api.worldview.purpose,
    },
    voice: {
      traits: api.voice.traits,
      description: api.voice.description,
      examplePhrases: api.voice.example_phrases,
      avoidPhrases: api.voice.avoid_phrases,
    },
    decisions: {
      claimOwnership: api.dimensions.claim_ownership,
      confidence: api.dimensions.confidence,
      callToAction: api.dimensions.call_to_action,
      vulnerability: api.dimensions.vulnerability,
      energy: api.dimensions.energy,
      formality: api.dimensions.formality,
      specificity: api.dimensions.specificity,
    },
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/**
 * Convert WizardPersona (from wizard components) to Persona for API submission
 */
export function wizardToPersona(
  wizard: import('../poc/wizards/types').WizardPersona,
  id?: string
): Omit<Persona, 'id' | 'createdAt' | 'updatedAt'> & { id?: string } {
  return {
    ...(id !== undefined && { id }),
    name: wizard.name,
    avatar: wizard.avatar,
    tagline: wizard.tagline,
    worldview: {
      coreBeliefs: wizard.worldview.coreBeliefs,
      values: wizard.worldview.values,
      audience: wizard.worldview.audience,
      purpose: wizard.worldview.purpose,
    },
    voice: {
      traits: wizard.voice.traits,
      description: wizard.voice.description,
      examplePhrases: wizard.voice.examplePhrases,
      avoidPhrases: wizard.voice.avoidPhrases,
    },
    decisions: {
      claimOwnership: wizard.decisions.claimOwnership,
      confidence: wizard.decisions.confidence,
      callToAction: wizard.decisions.callToAction,
      vulnerability: wizard.decisions.vulnerability,
      energy: wizard.decisions.energy,
      formality: wizard.decisions.formality,
      specificity: wizard.decisions.specificity,
    },
  };
}

/**
 * Convert Persona to WizardPersona for use with wizard components
 */
export function personaToWizard(persona: Persona): import('../poc/wizards/types').WizardPersona {
  return {
    name: persona.name,
    avatar: persona.avatar ?? '',
    tagline: persona.tagline ?? '',
    worldview: {
      coreBeliefs: persona.worldview.coreBeliefs,
      values: persona.worldview.values,
      audience: persona.worldview.audience,
      purpose: persona.worldview.purpose,
    },
    voice: {
      traits: persona.voice.traits,
      description: persona.voice.description,
      examplePhrases: persona.voice.examplePhrases,
      avoidPhrases: persona.voice.avoidPhrases,
    },
    decisions: persona.decisions as import('../poc/wizards/types').PersonaDecisions,
  };
}
